import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useStockMovements } from "@/hooks/useStockMovements";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";

const movementTypeLabels: Record<string, string> = {
  ENTRADA: "Entrada",
  SAIDA: "Saída",
  AJUSTE: "Ajuste",
};

const negativeTypes = new Set([
  "SAIDA",
]);

const positiveTypes = new Set([
  "ENTRADA",
]);

interface StockMovementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId?: string | null;
  itemName?: string | null;
}

export function StockMovementsDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
}: StockMovementsDialogProps) {
  const { t } = useTranslation("erp");
  const { data = [], isLoading, error } = useStockMovements(
    { itemId: itemId ?? undefined },
    { enabled: open && !!itemId, limit: 100 }
  );

  const title = itemName
    ? t("dialogs.stockMovements.titleWithItem", { item: itemName })
    : t("dialogs.stockMovements.title");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <span className="ml-2 text-muted-foreground">{t("common.loading")}</span>
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {t("dialogs.stockMovements.loadError")}
          </div>
        ) : data.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t("dialogs.stockMovements.empty")}
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dialogs.stockMovements.columns.date")}</TableHead>
                  <TableHead>{t("dialogs.stockMovements.columns.type")}</TableHead>
                  <TableHead className="text-right">{t("dialogs.stockMovements.columns.quantity")}</TableHead>
                  <TableHead className="text-right">{t("dialogs.stockMovements.columns.before")}</TableHead>
                  <TableHead className="text-right">{t("dialogs.stockMovements.columns.after")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((movement) => {
                  const movementType = movement.type;
                  const label = movementTypeLabels[movementType] || movementType;
                  const isNegative = negativeTypes.has(movementType);
                  const isPositive = positiveTypes.has(movementType);
                  const badgeVariant = isNegative ? "destructive" : isPositive ? "default" : "secondary";
                  const quantityValue = (isNegative ? -1 : 1) * Math.abs(Number(movement.quantity ?? 0));

                  return (
                    <TableRow key={movement.id}>
                      <TableCell className="whitespace-nowrap">
                        {movement.created_at
                          ? format(new Date(movement.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={badgeVariant}>{label}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {quantityValue.toLocaleString("pt-BR", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 3,
                        })}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {Number(movement.quantity_before ?? 0).toLocaleString("pt-BR", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 3,
                        })}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {Number(movement.quantity_after ?? 0).toLocaleString("pt-BR", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 3,
                        })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
